import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons'
import React, { ReactElement, useMemo, useCallback } from 'react'
import styles from './ClickableArrow.module.scss'
import Link from 'next/link'

interface BaseClickableArrowProps {
    orientation: 'left' | 'right'
    text?: string
    disabled?: boolean
}

interface ClickableArrowPropsLink extends BaseClickableArrowProps {
    linkDestination: string
}

interface ClickableArrowPropsWithCallback extends BaseClickableArrowProps {
    onClick: () => void
}

type ClickableArrowProps =
    | ClickableArrowPropsLink
    | ClickableArrowPropsWithCallback

const isLink = (
    clickableArrowProps: ClickableArrowProps
): clickableArrowProps is ClickableArrowPropsLink => {
    return 'linkDestination' in clickableArrowProps
}

/**
 * can either directly link or run a callback
 */
export const ClickableArrow = (props: ClickableArrowProps): ReactElement => {
    const onClick = useCallback(
        () => (isLink(props) || props.disabled ? undefined : props.onClick()),
        [props]
    )
    const className = useMemo(
        (): string =>
            `${styles.clickableArrow} ${props.disabled ? styles.disabled : ''}`,
        [props.disabled]
    )

    const arrow = useMemo(
        () =>
            props.orientation === 'left' ? (
                <>
                    <ArrowLeftOutlined
                        className={className}
                        onClick={onClick}
                    />
                    {props.text}
                </>
            ) : (
                <>
                    <ArrowRightOutlined
                        className={className}
                        onClick={onClick}
                    />
                    {props.text}
                </>
            ),
        [className, onClick, props.orientation, props.text]
    )
    if (isLink(props) && !props.disabled)
        return <Link href={props.linkDestination}>{arrow}</Link>
    return arrow
}
